import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { generatePath } from 'react-router-dom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { NewTripProvider, useNewTrip } from '../context/NewTripContext'
import useBusinessTripService from '../services/BusinessTripService'
import useDriverActivityService from '../services/DriverActivityService'
import { ActivityInfo } from '@/app/models/activityContainerModel'
import { HTML5Backend as Backend } from 'react-dnd-html5-backend'

import { CountryModel } from '@/app/models/timesheet/countryModel'
import { toast } from '@/lib/toasts/api'
import {
  DriversProvider,
  useDrivers,
} from '@/modules/shared/context/drivers/DriversContext'
import { TaskProvider } from '@/modules/shared/context/TaskContext'
import { driversRoutesMap } from '../routes'
import { ActivityModel } from '@/app/models'

import { EventModel } from '@/app/models/newDelegation/eventModel'
import { ensureUids } from '@/app/common/newDelegation/eventIdentity'
import { TmsDataModel } from '@/app/models/tmsDataModel'
import { GpsDataModel } from '@/app/models/gpsDataModel'
import { GpsFromTachographModel } from '@/app/models/gpsFromTachographModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TripGenerationMode } from '@/app/constants/delegation/tripGenerationMode'
import { getApiErrorMessage } from '@/modules/shared/utils/apiError'
import {
  CurrencyTableIssue,
  findCurrencyTableIssue,
  getCurrencyTableIssueDetails,
  getCurrencyTableIssueFromApiError,
  getCurrencyTableIssueTitle,
} from '@/modules/shared/utils/currencyTableIssue'
import { OperationResultType } from '@/app/constants/operationResultType'

import { CombinedImportView } from './components/businessTrip/CombinedImportView'
import { EventsErrorModal } from './components/businessTrip/EventsErrorModal'
import { TmsImportView } from './components/businessTrip/TmsImportView'
import { CardLoadsView } from './components/businessTrip/CardLoadsView'
import { BordersView } from './components/businessTrip/BordersView'
import { LegendModal } from '../modals/LegendModal'
import { faClose, FaIcon } from '@/lib/fa'
import { DndRoot } from '@/modules/shared/components/DndRoot'
import { CustomDragLayer } from '../components/businessTripChart'
import AddBusinessTripActionBar from '../tabs/businessTrips/addBusinessTrip/AddBusinessTripActionBar'
import AddBusinessTripNavigation from '../tabs/businessTrips/addBusinessTrip/AddBusinessTripNavigation'
import BusinessTripChartWrapperNew from '../tabs/businessTrips/addBusinessTrip/BusinessTripChartWrapperNew'
import AddBusinessTipNavbar from '../tabs/businessTrips/addBusinessTrip/AddBusinessTipNavbar'
import AddBusinessFooter from '../tabs/businessTrips/addBusinessTrip/AddBusinessFooter'
import { HOMELAND, toggleRestOnBaseEvents } from '../utils/restOnBase'
import {
  addTripEvent,
  editTripEvent,
  removeTripEvents,
} from '../utils/eventListEdits'
import { relinkTmsDocuments } from '../utils/eventTmsPair'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { RouteComponentProps, withRouter } from '@/app/navigation/withRouter'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'

const isOperationResultError = (r: any) => {
  if (r?.code && r.code !== 'SUCCESS') return true

  // `type` arrives as the numeric OperationResultType from SuperTacho.Api
  // (Newtonsoft serializes the enum as a number), but some endpoints send the
  // name instead - handle both.
  const typeValue = r?.type ?? r?.Type
  if (typeof typeValue === 'number') {
    return typeValue === OperationResultType.Error
  }
  if (typeof typeValue === 'string') return typeValue.toLowerCase() === 'error'

  return false
}

interface RouteParams {
  companyId: string
  driverId: string
  from: string
  to: string
}

interface DriverGroupBusinessTripNewPageContentProps
  extends RouteComponentProps<RouteParams> {

  companyIdOverride?: string
  driverIdOverride?: string
  fromOverride?: string
  toOverride?: string

  onClose?: (saved: boolean) => void
}

const DriverGroupBusinessTripNewPageContent: React.FC<
  DriverGroupBusinessTripNewPageContentProps
> = ({
  match,
  history,
  companyIdOverride,
  driverIdOverride,
  fromOverride,
  toOverride,
  onClose,
}) => {
  const { t } = useLocalize()
  const { params } = match
  const companyId = companyIdOverride ?? params.companyId
  const driverId = driverIdOverride ?? params.driverId
  const from = fromOverride ?? params.from
  const to = toOverride ?? params.to
  const isEmbedded = Boolean(onClose)

  const { events, moveEvent, resetEvents } = useNewTrip()


  const resetEventsWithUids = React.useCallback(
    (next: EventModel[]) =>
      resetEvents(ensureUids(relinkTmsDocuments(next))),
    [resetEvents],
  )

  const businessTripService = useBusinessTripService()
  const confirm = useConfirm()
  const { getDriverActivity } = useDriverActivityService()
  const { fetchDriver } = useDrivers()

  const locationState = history.location?.state as
    | {
        driverIds?: string[]
        returnTo?: { pathname: string; search?: string }
        creatorWizard?: {
          step: number
          subStep: number
          selectedDrivers?: unknown[]
        }
      }
    | undefined
  const multiDriverIds = locationState?.driverIds
  const isMultiDriverMode = multiDriverIds && multiDriverIds.length > 1
  const returnTo = locationState?.returnTo
  const creatorWizardResume = locationState?.creatorWizard

  const initialFetchDoneRef = React.useRef(false)
  const eventsRef = React.useRef(events)

  React.useEffect(() => {
    eventsRef.current = events
  }, [events])

  const [isRestsOnBaseModeOn, setIsRestsOnBaseModeOn] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [initialDriverLoadFinished, setInitialDriverLoadFinished] =
    React.useState(false)
  const [showCoDriversPopup, setShowCoDriversPopup] = React.useState(false)
  const [checkedTripCoDrivers, setCheckedTripCoDrivers] = React.useState<
    boolean[]
  >([])
  const [tripCoDriversMessage, setTripCoDriversMessage] = React.useState<
    string | null
  >(null)
  // Trzyma powód i datę brakującego kursu, bo od nich zależy treść okna - samo
  // "pokaż popup" nie miało czego powiedzieć użytkownikowi.
  const [currencyTableIssue, setCurrencyTableIssue] =
    React.useState<CurrencyTableIssue | null>(null)
  // Kolejne okna kontynuują tę samą próbę zapisu. Nowe kliknięcie „Zapisz”
  // przekazuje wartości początkowe i rozpoczyna zbieranie decyzji od nowa.
  const saveDecisionsRef = React.useRef({
    withPreviousExchangeRateDate: false,
    forceCopyToCoDriver: false,
    copyToCoDriver: false,
    checkedTripCoDrivers: [] as boolean[],
  })

  const [dateTo, setDateTo] = React.useState<Moment>(moment(to))
  const [canSaveEvents, setCanSaveEvents] = React.useState(true)
  const [saveErrorMessage, setSaveErrorMessage] = React.useState<string | null>(
    null,
  )
  const [showEventsError, setShowEventsError] = React.useState(false)
  const [showImport, setShowImport] = React.useState(false)
  const [showLegend, setShowLegend] = React.useState(false)
  const [showTmsLoadsModal, setShowTmsLoadsModal] = React.useState(false)
  const [showCardLoadsModal, setShowCardLoadsModal] = React.useState(false)
  const [showBordersModal, setShowBordersModal] = React.useState(false)

  const [countries, setCountries] = React.useState<CountryModel[]>([])
  const [countriesWithMinimalPayment, setCountriesWithMinimalPayment] =
    React.useState<string[]>([])
  const [vehicles, setVehicles] = React.useState<any[]>([])

  const [selectedDriver, setSelectedDriver] = React.useState<any>(null)

  const [data, setData] = React.useState<ActivityInfo | null>(null)

  const {
    activities = [],
    breaks = [],
    cardInserts = [],
    dailyPartialRegularRests = [],
    dailyRests = [],
    dayActivities = [],
    outOfScopes = [],
    existingDelegations = [],
    tripEvents = [],
    partialBreaks = [],
    places = [],
    violations = [],
    weekRecompenses = [],
    weeklyReducedRests = [],
    weeklyRests = [],
    notes = [],
  } = data || {}
  const [destinationCountry, setDestinationCountry] =
    React.useState<CountryModel>(HOMELAND)

  const [calculationDate, setCalculationDate] = React.useState<string | null>(
    null,
  )
  const [apiTmsData, setApiTmsData] = React.useState<TmsDataModel[]>([])
  const [apiGpsData, setApiGpsData] = React.useState<GpsDataModel[]>([])
  const [apiTachographData, setApiTachographData] = React.useState<
    GpsFromTachographModel[]
  >([])

  const dateFrom = React.useMemo(() => moment(from), [from])
  const dateToMoment = React.useMemo(() => moment(to), [to])

  const [chartStartTime, setChartStartTime] = React.useState<Moment>(() =>
    moment(from),
  )

  React.useEffect(() => {
    setChartStartTime(moment(from))
  }, [from])

  const handleStartTimeChange = React.useCallback((newDate: Moment) => {
    setChartStartTime(newDate)
  }, [])

  React.useEffect(() => {
    const fetchInitialData = async () => {
      if (!companyId) return

      setIsLoading(true)
      try {
        const [
          countriesData,
          countriesWithMinimalPaymentData,
          vehiclesData,
          driverData,
        ] = await Promise.all([
          businessTripService.getCountries({ companyId }),
          businessTripService.getCountriesWithMinimalPayment({ companyId }),
          businessTripService.getVehicles({ companyId }),
          fetchDriver({ driverId: Number(driverId) }),
        ])

        setCountries(countriesData)
        setCountriesWithMinimalPayment(countriesWithMinimalPaymentData)
        setVehicles(vehiclesData)
        setSelectedDriver(driverData)

        try {
          const tmsResult = await businessTripService.getTmsData({
            companyId,
            driverId,
            startTime: from,
            endTime: to,
          })
          setApiTmsData(tmsResult)
        } catch (tmsErr) {
          console.error('Error fetching TMS data:', tmsErr)
        }

        try {
          const [gpsResult, tachoResult] = await Promise.all([
            businessTripService.getGpsData({
              companyId,
              driverId,
              startTime: from,
              endTime: to,
            }),
            businessTripService.getGpsFromTachograph({
              companyId,
              driverId: String(driverId),
              startTime: from,
              endTime: to,
            }),
          ])
          setApiGpsData(gpsResult || [])
          setApiTachographData(tachoResult || [])
        } catch {}

        setDateTo(moment(to))
      } catch (err) {
        console.error('Error fetching initial data:', err)
        toast.error({
          text: t('error.loading'),
          title: getApiErrorMessage(err) || t('error.loading'),
        })
      } finally {
        setIsLoading(false)
        setInitialDriverLoadFinished(true)
      }
    }

    fetchInitialData()
  }, [companyId, driverId, from, to])

  React.useEffect(() => {
    if (!companyId || !driverId || !dateFrom || !dateFrom.isValid()) return
    if (initialFetchDoneRef.current) return
    initialFetchDoneRef.current = true

    const fetchInitialActivities = async () => {
      setIsLoading(true)
      try {
        const days = 31
        const adjustedStart = dateFrom.clone().subtract(days, 'days')
        const adjustedEnd = dateFrom.clone().add(days, 'days')

        const fetchedData = await getDriverActivity({
          companyId: companyId,
          driverId: Number(driverId),
          startTime: adjustedStart.format('YYYY-MM-DD'),
          endTime: adjustedEnd.format('YYYY-MM-DD'),
          includeTrips: true,
        })

        setData(fetchedData)
      } catch (err) {
        console.error('Error fetching initial driver activities:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchInitialActivities()
  }, [companyId, driverId, dateFrom])

  const handleSaveGrouped = React.useCallback(
    async (
      id: string,
      defaultStartDate: Moment,
      country: CountryModel,
      force: boolean,
      withPreviousExchangeRateDate: boolean,
      forceCopyToCoDriver: boolean,
      copyToCoDriver: boolean,
      checkedTripCoDrivers: boolean[],
    ) => {
      if (!companyId || !driverId) return

      saveDecisionsRef.current = {
        withPreviousExchangeRateDate,
        forceCopyToCoDriver,
        copyToCoDriver,
        checkedTripCoDrivers,
      }

      const driverIdsToSave =
        isMultiDriverMode && multiDriverIds && multiDriverIds.length > 0
          ? multiDriverIds
          : [String(driverId)]

      try {
        setIsLoading(true)

        const saveArgs = {
          companyId,
          id,
          defaultStartDate,
          calculationDate,
          country,
          events,
          force,
          withPreviousExchangeRateDate,
          forceCopyToCoDriver,
          copyToCoDriver,
          checkedTripCoDrivers,
        }

        const runSave = (ids: string[], forceSave: boolean) =>
          Promise.all(
            ids.map(async (dId) => ({
              driverId: dId,
              results: await businessTripService.saveGrouped({
                ...saveArgs,
                driverId: Number(dId),
                forceSave,
              }),
            })),
          )

        let perDriver = await runSave(driverIdsToSave, false)

        // Mirrors WPF TripGroupViewModel.OnSave(): overlapping *non-settled* trips
        // are a Yes/No question, not a dead end. Only the drivers that hit it are
        // retried, so the ones already saved are not written twice.
        const needsConfirmation = perDriver.filter(
          (d) =>
            Array.isArray(d.results) &&
            d.results.some(
              (r: any) => r.code === 'TRIPS_WITH_OVERLAPPING_DATES',
            ) &&
            d.results.every(
              (r: any) =>
                r.code === 'TRIPS_WITH_OVERLAPPING_DATES' ||
                !isOperationResultError(r),
            ),
        )

        if (needsConfirmation.length > 0) {
          const details = needsConfirmation
            .flatMap((d) =>
              d.results
                .filter((r: any) => r.code === 'TRIPS_WITH_OVERLAPPING_DATES')
                .map((r: any) => r.message)
                .filter(Boolean),
            )
            .join('\n')

          const heading = t('errorCode.TRIPS_WITH_OVERLAPPING_DATES')
          const accepted = await confirm({
            title: t('common.warning'),
            message: details ? `${heading}\n${details}` : heading,
            confirmText: t('answer.yes') || 'Tak',
            cancelText: t('answer.no') || 'Nie',
          })

          if (!accepted) return

          const retried = await runSave(
            needsConfirmation.map((d) => d.driverId),
            true,
          )
          perDriver = perDriver.map(
            (d) => retried.find((r) => r.driverId === d.driverId) ?? d,
          )
        }

        const allResults = perDriver.flatMap((d) =>
          Array.isArray(d.results) ? d.results : [],
        )
        if (Array.isArray(allResults) && allResults.length > 0) {
          const hasErrors = allResults.some(isOperationResultError)
          if (hasErrors) {
            const copyToCoDriver = allResults.find(
              (r: any) => r.code === 'COPY_TO_CODRIVER',
            )
            const settledOverlapping = allResults.find(
              (r: any) => r.code === 'SETTLED_TRIPS_WITH_OVERLAPPING_DATES',
            )
            const overlapping = allResults.find(
              (r: any) => r.code === 'TRIPS_WITH_OVERLAPPING_DATES',
            )
            const currencyIssue = findCurrencyTableIssue(allResults)
            if (copyToCoDriver) {
              const message = copyToCoDriver.message || ''
              const booleans: boolean[] = []
              message.split('|').forEach((trip: string) => {
                const idx = trip.indexOf(';')
                if (idx >= 0) {
                  const drivers = trip.substring(idx + 1)
                  drivers.split(',').forEach((d: string) => {
                    if (d && d.trim()) booleans.push(true)
                  })
                }
              })
              setTripCoDriversMessage(message)
              setCheckedTripCoDrivers(booleans)
              setShowCoDriversPopup(true)
              return
            } else if (currencyIssue) {
              setCurrencyTableIssue(currencyIssue)
              return
            } else if (settledOverlapping) {
              const details = t(
                'newDelegation.validation-settled-overlapping-trips-details',
              )
              const msg = settledOverlapping.message
                ? `${details} ${settledOverlapping.message}`
                : details
              setSaveErrorMessage(
                `${t('newDelegation.validation-settled-overlapping-trips-title')}: ${msg}`,
              )
            } else if (overlapping) {
              const details = t(
                'newDelegation.validation-overlapping-trips-details',
              )
              const msg = overlapping.message
                ? `${details} ${overlapping.message}`
                : details
              setSaveErrorMessage(
                `${t('newDelegation.validation-overlapping-trips-title')}: ${msg}`,
              )
            } else if (
              allResults.some((r: any) => r.code === 'MISSING_BEGIN_OR_END')
            ) {
              setCanSaveEvents(false)
              setShowEventsError(true)
            } else {
              const errorMessages = allResults
                .filter(isOperationResultError)
                .map(
                  (r: any) => r.message || t(`errorCode.${r.code}`) || r.code,
                )
                .join(', ')
              setSaveErrorMessage(
                `${t('businessTrip.save-failed')}: ${errorMessages}`,
              )
            }
            return
          }
        }

        toast.success({
          text: isMultiDriverMode
            ? t('businessTrip.multi-save-success').replace(
                '{0}',
                String(driverIdsToSave.length),
              )
            : t('businessTrip.save-success'),
        })

        if (onClose) {
          onClose(true)
          return
        }

        if (returnTo) {
          const target = returnTo.search
            ? `${returnTo.pathname}${returnTo.search}`
            : returnTo.pathname
          history.push(
            target,
            creatorWizardResume
              ? { resumeCreatorWizard: creatorWizardResume }
              : undefined,
          )
          return
        }

        const backPath = isMultiDriverMode
          ? generatePath(driversRoutesMap.driverGroupBusinessTrips, {
              companyId,
              from,
              to,
            })
          : generatePath(driversRoutesMap.driverBusinessTrips, {
              companyId,
              driverId,
              from,
              to,
            })
        history.push(backPath)
      } catch (err) {
        console.error('Error saving grouped business trip:', err)
        // Brak kursu potrafi wrócić także jako 400 — wtedy też należy się okno z
        // wyjaśnieniem i wyjściem awaryjnym, a nie goły komunikat serwera.
        const currencyIssue = getCurrencyTableIssueFromApiError(err)
        if (currencyIssue) {
          setCurrencyTableIssue(currencyIssue)
          return
        }
        const errMsg = getApiErrorMessage(err) || t('businessTrip.save-failed')
        setSaveErrorMessage(`${t('error.save')}: ${errMsg}`)
      } finally {
        setIsLoading(false)
      }
    },
    [
      companyId,
      driverId,
      isMultiDriverMode,
      multiDriverIds,
      events,
      calculationDate,
      businessTripService,
      confirm,
      history,
      from,
      to,
      t,
      setSaveErrorMessage,
      returnTo,
      creatorWizardResume,
      onClose,
    ],
  )

  const handleGetDriverActivities = React.useCallback(
    async (
      companyIdParam: string,
      driverIdParam: string,
      startTime: Moment,
      endTime: Moment,
    ): Promise<ReadonlyArray<ActivityModel>> => {
      try {
        const fetchedData = await getDriverActivity({
          companyId: companyIdParam,
          driverId: Number(driverIdParam),
          startTime: startTime.format('YYYY-MM-DD'),
          endTime: endTime.format('YYYY-MM-DD'),
          includeTrips: true,
        })

        setData(fetchedData)
        return fetchedData?.activities || []
      } catch (err) {
        console.error('Error fetching driver activities:', err)
        return []
      }
    },
    [getDriverActivity],
  )

  const handleGenerateTrips = React.useCallback(
    async (generationMode: number) => {
      if (!companyId || !driverId) return

      try {
        setIsLoading(true)
        const result = await businessTripService.generateTrips({
          companyId,
          driverId: Number(driverId),
          defaultStartDate: dateFrom,
          defaultEndDate: dateToMoment,
          events,
          generationMode,
        })

        const newEvents = result.map((event) => ({
          ...event,
          id: event.id || Math.floor(Math.random() * 2_147_483_647),
        }))
        const allEvents = [...events, ...newEvents]
        const sorted = allEvents.sort((a, b) => {
          if (a.date < b.date) return -1
          if (a.date > b.date) return 1
          return 0
        })

        // No synthetic Begin/Finish around TMS markers that fall outside a
        // delegation bracket. WPF leaves them unbracketed on purpose: the save
        // path (TripsFromItemsCreator/SaveGroupedBusinessTripRequestHandler,
        // GroupItemsToTrips) pulls the missing half of a TMS document into the
        // trip that holds the other half, matching on DocNumber - so a loading
        // dated before the period start, or an unloading after its end, is
        // already saved with the right trip. Wrapping them created zero-length
        // trips on those dates, which collided with already settled delegations
        // (SETTLED_TRIPS_WITH_OVERLAPPING_DATES) and blocked the save entirely.
        const normalized = sorted.map((e, i) => ({ ...e, lp: i }))
        resetEventsWithUids(normalized)

        if (result.length > 0) {
          toast.info({
            text: t('businessTrip.trips-generated'),
            title: t('businessTrip.events-generated-count').replace(
              '{0}',
              String(result.length),
            ),
          })
        } else {
          const modeKey = TripGenerationMode[generationMode]
          const modeTitleKey = `groupedRibbon.${modeKey.charAt(0).toLowerCase() + modeKey.slice(1)}Title`
          const modeTitle = t(modeTitleKey) || modeKey
          toast.warning({
            text: t('newDelegation.trips-not-generated-title'),
            title: t('newDelegation.trips-not-generated-details').replace(
              '{0}',
              modeTitle,
            ),
          })
        }
      } catch (err: any) {
        console.error('Error generating trips:', err)
        const errorData = err?.response?.data
        if (errorData?.code === 'NO_ACTIVE_CONTRACT_IN_PERIOD') {
          toast.error({
            text: t('newDelegation.trips-no-contract-title'),
            title: t('newDelegation.trips-no-contract-details'),
          })
          return
        }
        toast.error({
          text: t('error.generate'),
          title: getApiErrorMessage(err) || t('error.generate'),
        })
      } finally {
        setIsLoading(false)
      }
    },
    [
      companyId,
      driverId,
      dateFrom,
      dateToMoment,
      events,
      businessTripService,
      resetEventsWithUids,
      t,
    ],
  )

  const handleAddEvent = React.useCallback(
    (event: EventModel) => {
      const newEvents = addTripEvent(events, event, activities)

      const sortedEvents = newEvents.sort((a, b) => {
        if (a.date < b.date) return -1
        if (a.date > b.date) return 1
        return 0
      })

      const eventsWithLp = sortedEvents.map((e, index) => ({
        ...e,
        lp: index,
      }))

      if (
        eventsWithLp.length > 0 &&
        eventsWithLp[eventsWithLp.length - 1].tripEvent ===
          TripEventType.Finish &&
        eventsWithLp[eventsWithLp.length - 1].date
      ) {
        setDateTo(moment(eventsWithLp[eventsWithLp.length - 1].date))
      }

      resetEventsWithUids(eventsWithLp)
    },
    [events, activities, resetEventsWithUids],
  )

  const handleRestClick = React.useCallback(
    (rest: ActivityModel) => {
      const withLp = toggleRestOnBaseEvents({
        events: eventsRef.current,
        rest,
        activities,
        country: destinationCountry,
      })
      if (!withLp) return

      const last = withLp[withLp.length - 1]
      if (last?.tripEvent === TripEventType.Finish && last.date) {
        setDateTo(moment(last.date))
      }

      resetEventsWithUids(withLp)
    },
    [activities, destinationCountry, resetEventsWithUids],
  )

  const importedTmsDocNumbers = React.useMemo(
    () =>
      new Set(
        events
          .filter(
            (e) =>
              e.docNumber &&
              (e.tripEvent === TripEventType.Loading ||
                e.tripEvent === TripEventType.Unloading),
          )
          .map((e) => e.docNumber as string),
      ),
    [events],
  )

  const handleReplaceTmsEvents = React.useCallback(
    (newEvents: EventModel[]) => {
      const processed = newEvents.map((e) => ({ ...e, temporary: false }))

      const withoutTms = events.filter(
        (e) =>
          !(
            e.docNumber &&
            (e.tripEvent === TripEventType.Loading ||
              e.tripEvent === TripEventType.Unloading)
          ),
      )
      const merged = [...withoutTms, ...processed]

      const sorted = merged.sort((a, b) => {
        if (a.date < b.date) return -1
        if (a.date > b.date) return 1
        return 0
      })

      const withLp = sorted.map((e, index) => ({ ...e, lp: index }))

      if (
        withLp.length > 0 &&
        withLp[withLp.length - 1].tripEvent === TripEventType.Finish &&
        withLp[withLp.length - 1].date
      ) {
        setDateTo(moment(withLp[withLp.length - 1].date))
      }

      resetEventsWithUids(withLp)

      const firstNew = newEvents.find((e) => e.date)
      if (firstNew?.date) {
        setChartStartTime(moment(firstNew.date).startOf('day'))
      }
    },
    [events, resetEventsWithUids],
  )

  const handleAddEvents = React.useCallback(
    (newEvents: EventModel[]) => {
      const processed = newEvents.map((e) => ({ ...e, temporary: false }))
      const merged = [...events, ...processed]

      const sorted = merged.sort((a, b) => {
        if (a.date < b.date) return -1
        if (a.date > b.date) return 1
        return 0
      })

      const withLp = sorted.map((e, index) => ({ ...e, lp: index }))

      if (
        withLp.length > 0 &&
        withLp[withLp.length - 1].tripEvent === TripEventType.Finish &&
        withLp[withLp.length - 1].date
      ) {
        setDateTo(moment(withLp[withLp.length - 1].date))
      }

      resetEventsWithUids(withLp)

      const firstNew = newEvents.find((e) => e.date)
      if (firstNew?.date) {
        setChartStartTime(moment(firstNew.date).startOf('day'))
      }
    },
    [events, resetEventsWithUids],
  )

  const handleEditEvent = React.useCallback(
    (event: EventModel, isLocalChange?: boolean) => {
      const updatedEvents = editTripEvent(events, event, activities)

      const sortedEvents = updatedEvents.sort((a, b) => {
        if (a.date < b.date) return -1
        if (a.date > b.date) return 1
        return 0
      })

      const eventsWithLp = sortedEvents.map((e, index) => ({
        ...e,
        lp: index,
      }))

      if (
        eventsWithLp.length > 0 &&
        eventsWithLp[eventsWithLp.length - 1].tripEvent ===
          TripEventType.Finish &&
        eventsWithLp[eventsWithLp.length - 1].date
      ) {
        setDateTo(moment(eventsWithLp[eventsWithLp.length - 1].date))
      }

      resetEventsWithUids(eventsWithLp)
    },
    [events, activities, resetEventsWithUids],
  )

  const handleRemoveEvents = React.useCallback(
    (eventsToRemove: EventModel[]) => {
      const remainingEvents = removeTripEvents(events, eventsToRemove)

      const eventsWithLp = remainingEvents.map((e, index) => ({
        ...e,
        lp: index,
      }))

      resetEventsWithUids(eventsWithLp)
    },
    [events, resetEventsWithUids],
  )

  const trySaveAll = React.useCallback((): boolean => {
    const hasBegin = events.some((e) => e.tripEvent === TripEventType.Begin)
    const hasFinish = events.some((e) => e.tripEvent === TripEventType.Finish)
    const canSaveEventsResult = events.length > 0 && hasBegin && hasFinish
    setCanSaveEvents(canSaveEventsResult)

    if (!canSaveEventsResult) {
      setShowEventsError(true)
    }

    return canSaveEventsResult
  }, [events])

  const handleSaveClick = React.useCallback(
    async (e?: React.MouseEvent) => {
      e?.preventDefault()
      e?.stopPropagation()

      setSaveErrorMessage(null)

      // Wynik bierzemy prosto z `trySaveAll`. Wcześniej było `trySaveAll()`, a zaraz
      // po nim `canSave()`, które czytało `canSaveEvents` z domknięcia - czyli stan
      // sprzed `setCanSaveEvents` z tego samego kliknięcia. Efekt: pierwsze kliknięcie
      // po zepsuciu listy zdarzeń i tak wysyłało POST i wracało z 400 (a odwrotnie -
      // po naprawieniu listy pierwsze kliknięcie było blokowane).
      if (!trySaveAll()) {
        // Błąd przy samej liście zdarzeń zapala już `trySaveAll` (`setShowEventsError`).
        return
      }

      await handleSaveGrouped(
        '0', // id for new trip
        dateFrom,
        destinationCountry,
        false, // force
        false, // withPreviousExchangeRateDate
        false, // forceCopyToCoDriver
        false, // copyToCoDriver
        [], // checkedTripCoDrivers
      )
    },
    [trySaveAll, handleSaveGrouped, dateFrom, t],
  )

  if ((!initialDriverLoadFinished || isLoading) && !selectedDriver) {
    return (
      <div className="tw-absolute tw-inset-0 tw-z-[100] tw-grid tw-h-full tw-place-items-center tw-bg-black/40">
        <div className="tw-flex tw-items-center tw-gap-4 tw-text-center">
          <LoaderIcon className="tw-animate-spin" />
          <p className="tw-text-content-base">{t('gridPager.loadingText')}</p>
        </div>
      </div>
    )
  }

  if (initialDriverLoadFinished && !isLoading && !selectedDriver) {
    return (
      <div className="tw-absolute tw-inset-0 tw-z-[100] tw-grid tw-h-full tw-place-items-center tw-bg-black/40">
        <div className="tw-flex tw-h-fit tw-w-fit tw-max-w-[400px] tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-bg-white tw-p-4">
          <p className="tw-text-content-base">
            {t('error.driver-not-found') || 'Nie znaleziono kierowcy'}
          </p>
          <button
            className="tw-rounded-lg tw-bg-primary tw-px-3 tw-py-2 tw-text-white"
            onClick={() => {
              const backPath = generatePath(
                driversRoutesMap.driverBusinessTrips,
                {
                  companyId,
                  driverId,
                  from,
                  to,
                },
              )
              history.push(backPath)
            }}
          >
            {t('operation.back')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`${
        isEmbedded ? 'tw-fixed' : 'tw-absolute'
      } tw-inset-0 tw-z-[209] tw-bg-black/40`}
    >
      <div className="tw-absolute tw-bottom-0 tw-left-0 tw-z-[100] tw-flex tw-h-full tw-max-h-[95vh] tw-w-full tw-flex-1 tw-flex-col tw-rounded-t-lg tw-border-t-2 tw-border-base-300">
        <div className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-rounded-t-lg tw-bg-white">
          {isEmbedded && (
            <button
              type="button"
              onClick={() => onClose?.(false)}
              aria-label={t('common.close')}
              className="tw-absolute tw-right-3 tw-top-3 tw-z-[210] tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-base-200 tw-text-content-base hover:tw-bg-base-300"
            >
              <FaIcon icon={faClose} />
            </button>
          )}
          <AddBusinessTipNavbar
            companyId={companyId}
            driverId={driverId}
            from={from}
            to={to}
            driverName={selectedDriver?.fullName || ''}
            history={history}
            driverCount={isMultiDriverMode ? (multiDriverIds?.length ?? 1) : 1}
            destinationCountry={destinationCountry}
            onDestinationCountryChange={(country) =>
              setDestinationCountry((prev) => country ?? prev)
            }
            calculationDate={calculationDate}
            onCalculationDateChange={setCalculationDate}
            backPath={
              isMultiDriverMode
                ? generatePath(driversRoutesMap.driverGroupBusinessTrips, {
                    companyId,
                    from,
                    to,
                  })
                : undefined
            }
          />
          <DndRoot backend={Backend}>
            <CustomDragLayer />

            <AddBusinessTripActionBar
              showImport={showImport}
              companyId={companyId}
              isRestsOnBaseModeOn={isRestsOnBaseModeOn}
              setIsRestsOnBaseModeOn={setIsRestsOnBaseModeOn}
              driverId={Number(driverId)}
              dateFrom={dateFrom}
              dateToMoment={dateToMoment}
              onShowTmsLoads={() => setShowTmsLoadsModal(true)}
              onShowCardLoads={() => setShowCardLoadsModal(true)}
              onShowBorders={() => setShowBordersModal(true)}
              onShowEvents={() => setShowEventsError(true)}
              onGenerateTrips={handleGenerateTrips}
            />
            <AddBusinessTripNavigation
              dateFrom={chartStartTime}
              onDateChange={handleStartTimeChange}
            />

            <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto">

              {saveErrorMessage && (
                <div className="tw-mx-2 tw-mb-2 tw-flex tw-items-start tw-justify-between tw-gap-2 tw-rounded tw-border tw-border-red-300 tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-red-800">
                  <span>{saveErrorMessage}</span>
                  <button
                    className="tw-ml-2 tw-shrink-0 tw-text-red-500 hover:tw-text-red-700"
                    onClick={() => setSaveErrorMessage(null)}
                    aria-label="Zamknij"
                  >
                    <FaIcon icon={faClose} />
                  </button>
                </div>
              )}

              <div className="tw-flex-1 tw-overflow-hidden">
                <BusinessTripChartWrapperNew
                  companyId={companyId}
                  driverId={Number(driverId)}
                  tripId={undefined}
                  readonly={false}
                  groupMode={true}
                  dateFrom={dateFrom}
                  isRestsOnBaseModeOn={isRestsOnBaseModeOn}
                  dateTo={dateTo}
                  events={events}
                  addEvent={handleAddEvent}
                  removeEvents={handleRemoveEvents}
                  editEvent={handleEditEvent}
                  moveEvent={moveEvent}
                  hideDelegationMarkerPopups={() => {}}
                  markNewEvent={() => {}}
                  onRestClick={handleRestClick}
                  activities={activities}
                  breaks={breaks}
                  cardInserts={cardInserts}
                  dailyPartialRegularRests={dailyPartialRegularRests}
                  dailyRests={dailyRests}
                  dayActivities={dayActivities}
                  outOfScopes={outOfScopes}
                  existingDelegations={existingDelegations}
                  tripEvents={tripEvents}
                  partialBreaks={partialBreaks}
                  places={places}
                  violations={violations}
                  weekRecompenses={weekRecompenses}
                  weeklyReducedRests={weeklyReducedRests}
                  weeklyRests={weeklyRests}
                  notes={notes}
                  countries={countries}
                  countriesWithMinimalPayment={countriesWithMinimalPayment}
                  getDriverActivities={handleGetDriverActivities}
                  apiTmsData={apiTmsData}
                  apiGpsData={apiGpsData}
                  apiTachographData={apiTachographData}
                  showGpsOnGraph={false}
                  isLoading={isLoading}
                  number={''}
                  translate={(text: string) => t(text) || text}
                  startTime={chartStartTime}
                  onStartTimeChange={handleStartTimeChange}
                  noDndProvider={true}
                />
              </div>
            </div>
            <AddBusinessFooter onSave={handleSaveClick} />
          </DndRoot>
        </div>
        {showImport && (
          <CombinedImportView
            companyId={companyId}
            driverId={Number(driverId)}
            dateFrom={dateFrom}
            dateTo={dateTo}
            vehicles={vehicles}
            onClose={() => setShowImport(false)}
            onSave={(importedEvents) => {
              handleAddEvents(importedEvents)
              setShowImport(false)
            }}
            translate={(key) => t(key) || key}
          />
        )}

        <LegendModal isOpen={showLegend} onClose={() => setShowLegend(false)} />

        {showCoDriversPopup && (
          <div className="tw-absolute tw-inset-0 tw-z-[300] tw-flex tw-items-center tw-justify-center tw-bg-black/50">
            <div className="tw-w-[420px] tw-rounded-lg tw-bg-white tw-shadow-xl">
              <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-t-lg tw-bg-[#FFA928] tw-px-4 tw-py-3">
                <span className="tw-text-base tw-font-medium tw-text-white">
                  {t('newDelegation.delegations-for-copying-title')}
                </span>
              </div>
              <div className="tw-p-4">
                <p className="tw-mb-3 tw-text-sm tw-text-gray-700">
                  {t('newDelegation.delegations-for-copying-details')}
                </p>
                {tripCoDriversMessage && (
                  <div className="tw-mb-4 tw-flex tw-max-h-64 tw-flex-col tw-gap-1 tw-overflow-y-auto">
                    {(() => {
                      let globalIndex = 0
                      return tripCoDriversMessage
                        .split('|')
                        .map((tripSegment, i) => {
                          const sepIdx = tripSegment.indexOf(';')
                          if (sepIdx < 0) return null
                          const tripDates = tripSegment.substring(0, sepIdx)
                          const drivers = tripSegment
                            .substring(sepIdx + 1)
                            .split(',')
                            .filter((d) => d && d.trim())
                          if (drivers.length === 0) return null
                          return drivers.map((driver, j) => {
                            const checkIdx = globalIndex++
                            return (
                              <label
                                key={`${i}_${j}`}
                                className="tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-text-sm"
                              >
                                <input
                                  type="checkbox"
                                  className="tw-mt-0.5 tw-shrink-0"
                                  checked={
                                    checkedTripCoDrivers[checkIdx] ?? true
                                  }
                                  onChange={() => {
                                    const updated = [...checkedTripCoDrivers]
                                    updated[checkIdx] = !updated[checkIdx]
                                    setCheckedTripCoDrivers(updated)
                                  }}
                                />
                                <span>
                                  {t(
                                    'newDelegation.delegations-for-copying-item-delegation',
                                  )}{' '}
                                  <strong>{tripDates}</strong>{' '}
                                  {t(
                                    'newDelegation.delegations-for-copying-item-codriver',
                                  )}{' '}
                                  <strong>{driver.trim()}</strong>
                                </span>
                              </label>
                            )
                          })
                        })
                    })()}
                  </div>
                )}
                <div className="tw-flex tw-justify-end tw-gap-3">
                  <button
                    className="tw-rounded tw-bg-[#FFA928] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-[#e09520]"
                    onClick={() => {
                      setShowCoDriversPopup(false)
                      handleSaveGrouped(
                        '0',
                        dateFrom,
                        destinationCountry,
                        false,
                        saveDecisionsRef.current.withPreviousExchangeRateDate,
                        true, // forceCopyToCoDriver
                        false, // copyToCoDriver = false (don't copy)
                        [],
                      )
                    }}
                  >
                    {t('answer.cancel') || 'Nie kopiuj'}
                  </button>
                  <button
                    className="tw-rounded tw-bg-[#2448bb] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-[#1a3a9e]"
                    onClick={() => {
                      setShowCoDriversPopup(false)
                      handleSaveGrouped(
                        '0',
                        dateFrom,
                        destinationCountry,
                        false,
                        saveDecisionsRef.current.withPreviousExchangeRateDate,
                        true, // forceCopyToCoDriver
                        true, // copyToCoDriver = true (copy)
                        checkedTripCoDrivers,
                      )
                    }}
                  >
                    {t('answer.yes') || 'Kopiuj'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {currencyTableIssue && (
          <div className="tw-absolute tw-inset-0 tw-z-[300] tw-flex tw-items-center tw-justify-center tw-bg-black/50">
            <div className="tw-w-[460px] tw-rounded-lg tw-bg-white tw-shadow-xl">
              <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-t-lg tw-bg-[#FFA928] tw-px-4 tw-py-3">
                <span className="tw-text-base tw-font-medium tw-text-white">
                  {getCurrencyTableIssueTitle(currencyTableIssue, t)}
                </span>
              </div>
              <div className="tw-p-4">
                <p className="tw-mb-2 tw-text-sm tw-text-gray-700">
                  {getCurrencyTableIssueDetails(currencyTableIssue, t)}
                </p>
                <p className="tw-mb-4 tw-text-sm tw-text-gray-500">
                  {t('newDelegation.use-last-available-rate-hint')}
                </p>
                <div className="tw-flex tw-justify-end tw-gap-3">
                  <button
                    className="tw-rounded tw-border tw-border-gray-300 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50"
                    onClick={() => setCurrencyTableIssue(null)}
                  >
                    {t('answer.cancel')}
                  </button>
                  {/* Odpowiednik "Spróbuj ponownie" z WPF-owego ExchangeRateMissingDialog:
                      ma sens tylko przy awarii NBP - przy braku tabeli na dany dzień
                      i przy dacie z przyszłości kolejna próba nic nie zmieni. */}
                  {currencyTableIssue.code === 'CURRENCY_SERVICE_UNAVAILABLE' && (
                    <button
                      className="tw-rounded tw-border tw-border-gray-300 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50"
                      onClick={() => {
                        setCurrencyTableIssue(null)
                        handleSaveGrouped(
                          '0',
                          dateFrom,
                          destinationCountry,
                          false,
                          false, // withPreviousExchangeRateDate
                          saveDecisionsRef.current.forceCopyToCoDriver,
                          saveDecisionsRef.current.copyToCoDriver,
                          saveDecisionsRef.current.checkedTripCoDrivers,
                        )
                      }}
                    >
                      {t('newDelegation.download-exchange-rate-again')}
                    </button>
                  )}
                  <button
                    className="tw-rounded tw-bg-[#FFA928] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-[#e09520]"
                    onClick={() => {
                      setCurrencyTableIssue(null)
                      handleSaveGrouped(
                        '0',
                        dateFrom,
                        destinationCountry,
                        false,
                        true, // withPreviousExchangeRateDate
                        saveDecisionsRef.current.forceCopyToCoDriver,
                        saveDecisionsRef.current.copyToCoDriver,
                        saveDecisionsRef.current.checkedTripCoDrivers,
                      )
                    }}
                  >
                    {t('newDelegation.use-last-available-rate')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showTmsLoadsModal && (
          <div className="tw-absolute tw-inset-0 tw-z-[200] tw-bg-white">
            <TmsImportView
              companyId={companyId}
              driverId={Number(driverId)}
              dateFrom={dateFrom}
              dateTo={dateTo}
              importedDocNumbers={importedTmsDocNumbers}
              onClose={() => setShowTmsLoadsModal(false)}
              onSave={(importedEvents) => {
                handleReplaceTmsEvents(importedEvents)
                setShowTmsLoadsModal(false)
              }}
              translate={(key) => t(key) || key}
            />
          </div>
        )}

        {showCardLoadsModal && (
          <div className="tw-absolute tw-inset-0 tw-z-[200] tw-bg-white">
            <CardLoadsView
              companyId={companyId}
              driverId={Number(driverId)}
              driverName={selectedDriver?.fullName}
              dateFrom={dateFrom}
              dateTo={dateTo}
              existingEvents={events}
              onClose={() => setShowCardLoadsModal(false)}
              onSave={(cardEvents) => {
                handleAddEvents(cardEvents)
                setShowCardLoadsModal(false)
              }}
            />
          </div>
        )}

        {showEventsError && (
          <div className="tw-absolute tw-inset-0 tw-z-[200]">
            <EventsErrorModal
              companyId={companyId}
              driverId={driverId}
              showError={!canSaveEvents}
              activities={activities}
              onClose={() => setShowEventsError(false)}
            />
          </div>
        )}

        {showBordersModal && (
          <div className="tw-absolute tw-inset-0 tw-z-[200] tw-bg-white">
            <BordersView
              companyId={companyId}
              driverId={Number(driverId)}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onClose={() => setShowBordersModal(false)}
              onSave={(importedEvents) => {
                handleAddEvents(importedEvents)
                setShowBordersModal(false)
              }}
              translate={(key) => t(key) || key}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const DriverGroupBusinessTripNewPage: React.FC<
  RouteComponentProps<RouteParams>
> = (props) => {
  return (
    <NewTripProvider>
      <DriverGroupBusinessTripNewPageContent {...props} />
    </NewTripProvider>
  )
}


type ModalProps = {
  companyId: string
  driverId: string
  from: string
  to: string
  onClose: (saved: boolean) => void
}

const DriverGroupBusinessTripNewModalInner: React.FC<
  RouteComponentProps<RouteParams> & ModalProps
> = ({ companyId, driverId, from, to, onClose, ...routerProps }) => {
  return (
    <TaskProvider>
      <DriversProvider>
        <NewTripProvider>
          <DriverGroupBusinessTripNewPageContent
            {...routerProps}
            companyIdOverride={companyId}
            driverIdOverride={driverId}
            fromOverride={from}
            toOverride={to}
            onClose={onClose}
          />
        </NewTripProvider>
      </DriversProvider>
    </TaskProvider>
  )
}

export const DriverGroupBusinessTripNewModal = withRouter(
  DriverGroupBusinessTripNewModalInner,
)

export default withRouter(DriverGroupBusinessTripNewPage)
